import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { publicAPI } from '@/services/api';

const OpenAccessPromoContext = createContext(null);

const POLL_MS = 5000;

export function OpenAccessPromoProvider({ children }) {
  const [promo, setPromo] = useState(null);
  const [tick, setTick] = useState(() => Date.now());

  const refresh = useCallback(async () => {
    try {
      const { data } = await publicAPI.getOpenAccessPromo();
      setPromo(data || null);
    } catch {
      setPromo((p) => (p?.active ? { ...p, active: false, seconds_remaining: 0 } : p));
    }
  }, []);

  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    return () => clearInterval(poll);
  }, [refresh]);

  useEffect(() => {
    if (!promo?.active) return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [promo?.active]);

  const secondsRemaining = useMemo(() => {
    if (!promo?.active || !promo?.ends_at) return promo?.seconds_remaining ?? 0;
    const end = new Date(promo.ends_at).getTime();
    return Math.max(0, Math.floor((end - tick) / 1000));
  }, [promo, tick]);

  const value = useMemo(
    () => ({
      promo,
      active: Boolean(promo?.active),
      secondsRemaining,
      requiresRegistration: promo?.requires_registration !== false,
      aiExcludedDuringPromo: promo?.ai_excluded_during_promo !== false,
      refresh,
    }),
    [promo, secondsRemaining, refresh],
  );

  return (
    <OpenAccessPromoContext.Provider value={value}>
      {children}
    </OpenAccessPromoContext.Provider>
  );
}

export function useOpenAccessPromo() {
  const ctx = useContext(OpenAccessPromoContext);
  if (!ctx) {
    throw new Error('useOpenAccessPromo must be used within OpenAccessPromoProvider');
  }
  return ctx;
}
